import { BlobReader, BlobWriter, ZipReader, ZipWriter, TextWriter, TextReader } from '@zip.js/zip.js';

/**
 * Patches ag-grids exported xlsx to make images move with cells
 *
 * ag-grid hardcodes editAs="absolute" on all image anchors in the drawing XML,
 * which means images float and dont move when rows are resized or reordered.
 * Changing it to "oneCell" makes images move (but not resize) with their cell.
 *
 * So, we are just unzipping the xlsx, replacing editAs="absolute" with editAs="oneCell"
 * in any drawing XML files, and rezipping it. AG-Grid does not have a solution for this.
 */
export async function patchExcelImageAnchors(blob: Blob): Promise<Blob> {
  // unzip the xlsx and read all entries
  const zipReader = new ZipReader(new BlobReader(blob));
  const entries = await zipReader.getEntries();
  await zipReader.close();

  const zipWriter = new ZipWriter(new BlobWriter('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'));

  for (const entry of entries) {
    const isDirectory = entry.directory;
    const isDrawing = entry.filename.startsWith('xl/drawings/drawing') && entry.filename.endsWith('.xml');

    if (isDirectory) {
      // Preserve directory entries to ensure the structure of the xlsx remains intact, excel requires
      await zipWriter.add(entry.filename, undefined, {
        lastModDate: entry.lastModDate,
        directory: true,
      });
    } else if (isDrawing && 'getData' in entry) {
      // patch the drawing XML — change image anchor from floating to "move with cells"
      let xml = await entry.getData(new TextWriter());
      xml = xml.replace(/editAs="absolute"/g, 'editAs="oneCell"');
      await zipWriter.add(entry.filename, new TextReader(xml), {
        lastModDate: entry.lastModDate,
      });
    } else if ('getData' in entry) {
      // pass thru all other files unchanged
      const fileBlob = await entry.getData(new BlobWriter());
      await zipWriter.add(entry.filename, new BlobReader(fileBlob), {
        lastModDate: entry.lastModDate,
      });
    }
  }

  return zipWriter.close() as Promise<Blob>;
}
